import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getConnectedPlugins, PluginStatus, PluginAggregates } from "@/services/pluginStatusApi";
import { getPluginLogsStats, type PluginLogsStats } from "@/services/pluginLogsApi";
import {
  getGlobalPause,
  setAutomationPause,
  setGlobalPause,
  setWorkerScraping,
  type AutomationPauseMode,
  type GlobalPauseState,
} from "@/services/adminApi";
import { PluginDetailSheet } from "./PluginDetailSheet";
import { PluginDistributionCharts } from "./charts/PluginDistributionCharts";
import {
  ageSeconds,
  formatAge,
  heartbeatAgeColor,
  isWorkerErrorAlert,
  nextJobAuthLabel,
  workerActivityState,
} from "./workerState";
import { formatBrowser, parseBrowser } from "@/lib/userAgent";
import { formatOs } from "@/lib/os";
import { CountryFlag } from "@/components/CountryFlag";
import { BrowserIcon } from "@/components/icons/BrowserIcon";
import { OsIcon } from "@/components/icons/OsIcon";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Pause, ScrollText, ShieldAlert } from "lucide-react";

// Le tableau peut lister des centaines de plugins connectés : le tri (dernière
// activité) + le découpage 100/page sont faits côté back (?page=&page_size=),
// donc on ne charge/enrichit qu'une page à la fois. Les charts + la bannière
// checkpoint, eux, couvrent toute la flotte (calculés côté backoffice).
const PAGE_SIZE = 100;

export default function PluginStatusPage() {
  // `plugins` = la PAGE courante (le back trie par dernière activité + découpe).
  const [plugins, setPlugins] = useState<PluginStatus[]>([]);
  // Total fleet-wide (≠ longueur de la page) — compteur + pagination.
  const [total, setTotal] = useState(0);
  // Répartitions fleet-wide pour les 4 charts (calculées côté backoffice).
  const [aggregates, setAggregates] = useState<PluginAggregates | null>(null);
  // Workers en pause « Vérification LinkedIn » — fleet-wide (le back les renvoie
  // hors pagination pour que la bannière reste exhaustive).
  const [pausedPlugins, setPausedPlugins] = useState<PluginStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Drawer de détail : on stocke le worker_id (pas l'objet) pour que le contenu
  // du drawer suive le refresh 10s (quotas live) au lieu de figer un snapshot.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Clé du toggle scraping en cours de sauvegarde (`worker:{id}` /
  // `account:{user_id}`) — désactive le bon Switch pendant le PATCH.
  const [scrapingPending, setScrapingPending] = useState<string | null>(null);
  // Agrégat 24h des logs plugins (plugin_error_logs) — carte « Erreurs
  // récentes » au-dessus du tableau. Best-effort, suit le refresh 10s.
  const [logsStats, setLogsStats] = useState<PluginLogsStats | null>(null);
  // Switch d'urgence flotte entière (Redis global_automation_pause, lu en
  // tête de chaque /next-job back/ — propagation <=5s). Armement type-to-confirm.
  const [globalPause, setGlobalPauseState] = useState<GlobalPauseState | null>(null);
  const [globalArmText, setGlobalArmText] = useState("");
  const [globalReason, setGlobalReason] = useState("");
  const [savingGlobal, setSavingGlobal] = useState(false);
  // Page courante du tableau (1-indexée).
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, total);

  const loadPlugins = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getConnectedPlugins(navigate, { page, pageSize: PAGE_SIZE });
      setPlugins(data.plugins);
      setTotal(data.total);
      setAggregates(data.aggregates ?? null);
      setPausedPlugins(data.paused ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors du chargement des plugins");
    } finally {
      setLoading(false);
    }
    // Best-effort : l'état global suit le même refresh 10s.
    getGlobalPause(navigate)
      .then(setGlobalPauseState)
      .catch(() => {});
    // Best-effort : agrégat 24h des logs plugins (requête indexée created_at).
    getPluginLogsStats(navigate, 1)
      .then(setLogsStats)
      .catch(() => {});
  };

  const handleSetGlobalPause = async (mode: AutomationPauseMode | "none") => {
    if (mode !== "none") {
      if (globalArmText.trim().toUpperCase() !== "PAUSE") {
        toast({
          title: "Armement requis",
          description: "Tapez PAUSE dans le champ d'armement pour activer le switch global.",
          variant: "destructive",
        });
        return;
      }
      if (!globalReason.trim()) {
        toast({
          title: "Raison requise",
          description: "Indiquez la raison de la pause flotte (note interne).",
          variant: "destructive",
        });
        return;
      }
    }
    const ok = await confirmDialog({
      title:
        mode === "none"
          ? "Réactiver TOUTE la flotte"
          : mode === "scraping"
            ? "Suspendre le sourcing de TOUTE la flotte"
            : "Tout suspendre pour TOUTE la flotte",
      description:
        mode === "none"
          ? "Tous les plugins recevront à nouveau des jobs. Les files accumulées repartent (bornées par les quotas)."
          : mode === "scraping"
            ? "Plus aucun job de sourcing (enrichissements, recherches, offres) ne sera servi à AUCUN plugin, authentifié ou anonyme. Les campagnes continuent."
            : "Plus AUCUN job (sourcing, invitations, messages, checks) ne sera servi à AUCUN plugin. Toutes les campagnes de tous les clients seront figées tant que la pause dure.",
      confirmLabel: mode === "none" ? "Réactiver la flotte" : "SUSPENDRE LA FLOTTE",
      destructive: mode !== "none",
    });
    if (!ok) return;
    setSavingGlobal(true);
    try {
      const state = await setGlobalPause(
        { mode, reason: mode === "none" ? null : globalReason.trim() },
        navigate
      );
      setGlobalPauseState(state);
      setGlobalArmText("");
      setGlobalReason("");
      toast({
        title:
          mode === "none"
            ? "Flotte réactivée"
            : mode === "scraping"
              ? "Sourcing suspendu — flotte entière"
              : "Automatisation suspendue — flotte entière",
      });
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Mise à jour impossible",
        variant: "destructive",
      });
    } finally {
      setSavingGlobal(false);
    }
  };

  // Kill-switch scraping par plugin (worker_settings) — confirmation à la
  // désactivation seulement ; le refresh auto (10s) resynchronise ensuite.
  const handleToggleWorkerScraping = async (plugin: PluginStatus, enabled: boolean) => {
    const disabled = !enabled;
    if (disabled) {
      const ok = await confirmDialog({
        title: "Désactiver le scraping de ce plugin",
        description: `Le plugin ${plugin.worker_id.substring(0, 8)}… ne recevra plus de jobs de scraping (enrichissements candidats/écoles/entreprises, recherches, offres d'emploi). Les invitations et messages de campagne continuent.`,
        confirmLabel: "Désactiver",
        destructive: true,
      });
      if (!ok) return;
    }
    const key = `worker:${plugin.worker_id}`;
    setScrapingPending(key);
    try {
      await setWorkerScraping(plugin.worker_id, disabled, navigate);
      toast({
        title: disabled ? "Scraping du plugin désactivé" : "Scraping du plugin réactivé",
      });
      await loadPlugins();
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Mise à jour impossible",
        variant: "destructive",
      });
    } finally {
      setScrapingPending(null);
    }
  };

  // Kill-switch par COMPTE (automation pause v1, mode 'scraping') — affecte
  // tous les plugins du compte, authentifiés ET anonymes (miroir Redis).
  const handleToggleAccountScraping = async (plugin: PluginStatus, enabled: boolean) => {
    if (!plugin.user_id) return;
    const disabled = !enabled;
    if (disabled) {
      const ok = await confirmDialog({
        title: "Suspendre le sourcing du compte",
        description: `Coupe les jobs de scraping pour TOUS les plugins de ${plugin.user_name || plugin.user_id.substring(0, 8) + "…"} — authentifiés et anonymes. Les invitations, messages et checks de campagne continuent.`,
        confirmLabel: "Suspendre",
        destructive: true,
      });
      if (!ok) return;
    }
    const key = `account:${plugin.user_id}`;
    setScrapingPending(key);
    try {
      await setAutomationPause(
        plugin.user_id,
        disabled
          ? { mode: "scraping", reason: "Suspension depuis la page plugins connectés" }
          : { mode: "none" },
        navigate
      );
      toast({
        title: disabled ? "Sourcing du compte suspendu" : "Sourcing du compte réactivé",
      });
      await loadPlugins();
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Mise à jour impossible",
        variant: "destructive",
      });
    } finally {
      setScrapingPending(null);
    }
  };

  // Charge la page courante + refresh toutes les 10s. Re-souscrit quand `page`
  // change (clic pagination) -> fetch immédiat de la nouvelle page.
  useEffect(() => {
    loadPlugins();
    const interval = setInterval(loadPlugins, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Si la flotte rétrécit (refresh) sous la page courante, on recule d'office
  // pour ne pas rester bloqué sur une page vide.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount, page]);

  const formatResetCountdown = (resetsAt: string) => {
    const target = new Date(resetsAt).getTime();
    const diffMs = target - Date.now();
    if (diffMs <= 0) return "imminent";
    const totalMin = Math.floor(diffMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `dans ${h}h ${m}m`;
    return `dans ${m}m`;
  };

  const formatLastSeen = (lastSeen: string) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);

    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  };

  // Heartbeat colour and « Worker actif » badge: server-computed state and ages
  // (`worker_state`, `last_seen_age_s`, `activity_age_s` — see ./workerState.ts).
  // The admin browser's clock is never compared with back timestamps any more:
  // a drifting laptop used to paint healthy workers red.

  return (
    <TooltipProvider delayDuration={150}>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Plugins connectés</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Card className={globalPause?.mode ? "border-red-500" : undefined}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Pause className={`h-4 w-4 ${globalPause?.mode ? "text-red-600" : "text-muted-foreground"}`} />
                Interrupteur d'urgence — flotte entière
              </CardTitle>
              <CardDescription>
                Coupe le dispatch de jobs pour TOUS les plugins (authentifiés et
                anonymes) en moins de 5 secondes. À n'utiliser qu'en cas d'incident
                (vague de restrictions LinkedIn, bug d'enrichissement, …).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {globalPause?.mode ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-400 bg-red-50 p-3 text-red-900">
                  <div className="min-w-0">
                    <div className="font-semibold">
                      {globalPause.mode === "all"
                        ? "FLOTTE ENTIÈREMENT SUSPENDUE"
                        : "Sourcing suspendu pour toute la flotte"}
                    </div>
                    <div className="mt-0.5 text-xs">
                      {globalPause.by ? `par ${globalPause.by}` : ""}
                      {globalPause.at
                        ? ` le ${new Date(globalPause.at).toLocaleString("fr-FR")}`
                        : ""}
                      {globalPause.reason ? ` — ${globalPause.reason}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={savingGlobal}
                    onClick={() => handleSetGlobalPause("none")}
                  >
                    {savingGlobal ? "…" : "Réactiver la flotte"}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      Raison (note interne)
                    </div>
                    <Input
                      value={globalReason}
                      onChange={(e) => setGlobalReason(e.target.value)}
                      placeholder="Incident en cours…"
                      className="w-56"
                      maxLength={500}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      Tapez PAUSE pour armer
                    </div>
                    <Input
                      value={globalArmText}
                      onChange={(e) => setGlobalArmText(e.target.value)}
                      placeholder="PAUSE"
                      className="w-28"
                      aria-label="Armement du switch global"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={savingGlobal || globalArmText.trim().toUpperCase() !== "PAUSE"}
                    onClick={() => handleSetGlobalPause("scraping")}
                  >
                    Suspendre le sourcing
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={savingGlobal || globalArmText.trim().toUpperCase() !== "PAUSE"}
                    onClick={() => handleSetGlobalPause("all")}
                  >
                    Tout suspendre
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Répartition de TOUTE la flotte connectée (pas seulement la page
              affichée) : état (muet/actif/quota…), localisation, navigateur,
              OS, version. Les comptes sont calculés côté backoffice (champ
              `aggregates`, incl. `by_state` fleet-wide + ghosts). */}
          {aggregates && total > 0 && (
            <PluginDistributionCharts aggregates={aggregates} total={total} />
          )}

          {/* Erreurs récentes de la flotte (24h, table plugin_error_logs) :
              top raisons + mix de sévérité. La page /plugin-logs porte les
              filtres et le détail (stack + contexte). */}
          {logsStats && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <ScrollText className="h-4 w-4 text-muted-foreground" />
                  Erreurs récentes (24h)
                  <span className="font-normal text-muted-foreground">
                    {logsStats.total} événement{logsStats.total > 1 ? "s" : ""}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-7"
                    onClick={() => navigate("/plugin-logs")}
                  >
                    Tous les logs
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs">
                {logsStats.total === 0 ? (
                  <span className="text-muted-foreground">
                    Aucun événement remonté par la flotte sur les dernières 24h.
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    {logsStats.by_severity.map((b) => (
                      <span key={b.key} className="inline-flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 rounded-full ${
                            b.key === "error"
                              ? "bg-red-500"
                              : b.key === "warning"
                                ? "bg-amber-500"
                                : "bg-muted-foreground/50"
                          }`}
                        />
                        {b.key} · {b.count}
                      </span>
                    ))}
                    <Separator orientation="vertical" className="h-4" />
                    {logsStats.by_reason.slice(0, 5).map((b) => (
                      <code key={b.key} className="text-muted-foreground">
                        {b.key} ×{b.count}
                      </code>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Plugins connectés</CardTitle>
              <CardDescription>
                Liste des plugins actuellement connectés au système (mise à jour automatique toutes les 10 secondes)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Bandeau très visible : workers en pause sur une « Vérification
                  LinkedIn requise » (interstitiel de sécurité détecté côté
                  plugin -> automatisation en pause jusqu'à résolution manuelle
                  dans le navigateur). Remonté via heartbeat (cf.
                  PluginStatus.linkedin_checkpoint). */}
              {(() => {
                // Fleet-wide (renvoyé par le back hors pagination) : la bannière
                // ne doit pas rater un worker en pause sur une autre page.
                const paused = pausedPlugins;
                if (paused.length === 0) return null;
                const fmtSince = (ms?: number | null) =>
                  !ms ? null : formatLastSeen(new Date(ms).toISOString());
                return (
                  <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-amber-900">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <div className="min-w-0 text-sm">
                      <div className="font-semibold">
                        Vérification LinkedIn requise — {paused.length} worker
                        {paused.length > 1 ? "s" : ""} en pause
                      </div>
                      <div className="mt-0.5 text-amber-800">
                        LinkedIn demande une vérification de sécurité.
                        L'automatisation de {paused.length > 1 ? "ces workers" : "ce worker"}{" "}
                        est en pause et reprendra une fois la vérification résolue
                        dans le navigateur.
                      </div>
                      <ul className="mt-1.5 space-y-0.5 text-xs">
                        {paused.map((p) => {
                          const since = fmtSince(p.linkedin_checkpoint?.detected_at);
                          const who =
                            p.user_name ||
                            (p.linkedin_user?.firstName
                              ? `${p.linkedin_user.firstName} ${p.linkedin_user.lastName ?? ""}`.trim()
                              : p.is_anonymous
                              ? "anonyme"
                              : null);
                          return (
                            <li key={p.worker_id}>
                              <span className="font-mono">{p.worker_id.substring(0, 8)}…</span>
                              {who ? ` · ${who}` : ""}
                              {p.linkedin_checkpoint?.type ? ` · ${p.linkedin_checkpoint.type}` : ""}
                              {since ? ` · en pause depuis ${since}` : ""}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                );
              })()}

              {loading && plugins.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Chargement...
                </div>
              )}

              {error && (
                <div className="text-center py-8 text-red-500">
                  {error}
                </div>
              )}

              {!loading && total === 0 && !error && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun plugin connecté
                </div>
              )}

              {plugins.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Worker ID</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Localisation</TableHead>
                        <TableHead>Navigateur</TableHead>
                        <TableHead>Version plugin</TableHead>
                        <TableHead>OS</TableHead>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Profil LinkedIn</TableHead>
                        <TableHead>Quotas</TableHead>
                        <TableHead>Reset quotas</TableHead>
                        <TableHead>Dernière activité</TableHead>
                        <TableHead>Worker actif</TableHead>
                        <TableHead>Scraping</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plugins.map((plugin) => (
                        <TableRow
                          key={plugin.worker_id}
                          // Clic sur la ligne -> drawer de détail (worker / compte
                          // LinkedIn / compte RecrutAuto / quotas).
                          onClick={() => setSelectedId(plugin.worker_id)}
                          // Ligne surlignée quand le worker est en pause sur une
                          // « Vérification LinkedIn requise » — repérable d'un coup d'œil.
                          className={`cursor-pointer ${
                            plugin.linkedin_checkpoint?.active
                              ? "bg-amber-50 hover:bg-amber-100"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <TableCell className="font-mono text-xs">
                            {plugin.worker_id.substring(0, 8)}...
                          </TableCell>
                          <TableCell>{plugin.ip}</TableCell>
                          <TableCell>
                            {plugin.location ? (
                              <div className="text-sm flex items-center gap-1.5">
                                <CountryFlag location={plugin.location} />
                                <span>
                                  {plugin.location.city && `${plugin.location.city}, `}
                                  {plugin.location.country}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <BrowserIcon
                                name={
                                  parseBrowser(plugin.user_agent)?.name ||
                                  plugin.browser_name
                                }
                              />
                              <span>
                                {formatBrowser(
                                  plugin.user_agent,
                                  plugin.browser_name,
                                  plugin.browser_version,
                                )}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {plugin.plugin_version ? (
                              <Badge variant="outline">{plugin.plugin_version}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <OsIcon name={plugin.os_name} />
                              <span>{formatOs(plugin.os_name, plugin.os_version)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {plugin.is_anonymous ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="cursor-help">
                                    Anonyme
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  Plugin non rattaché à un compte RecrutAuto (pas de
                                  JWT). Ne reçoit que les jobs des queues publiques
                                  (search, enrichment, company/school enrichment,
                                  company_jobs) et seulement si un utilisateur
                                  authentifié en a posté. Sinon : heartbeat OK,
                                  aucun scrape.
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Badge variant="default">
                                {plugin.user_name || plugin.user_id?.substring(0, 8) + "..."}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {plugin.linkedin_user?.firstName ? (
                              <div className="text-sm">
                                <div className="font-medium">
                                  {plugin.linkedin_user.firstName} {plugin.linkedin_user.lastName}
                                </div>
                                {plugin.linkedin_user.occupation && (
                                  <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                                    {plugin.linkedin_user.occupation}
                                  </div>
                                )}
                                {plugin.linkedin_user.publicIdentifier && (
                                  <a
                                    href={`https://www.linkedin.com/in/${plugin.linkedin_user.publicIdentifier}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs text-blue-500 hover:underline"
                                  >
                                    {plugin.linkedin_user.publicIdentifier}
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {plugin.quotas ? (
                              <div
                                className={`text-xs ${plugin.quotas.stale ? "text-muted-foreground italic" : ""}`}
                                title={
                                  plugin.quotas.stale
                                    ? "Compteurs d'un jour précédent — seront remis à 0 au prochain poll worker"
                                    : undefined
                                }
                              >
                                <div>
                                  Inv {plugin.quotas.invites.used}/{plugin.quotas.invites.limit}
                                </div>
                                <div>
                                  Msg {plugin.quotas.messages.used}/{plugin.quotas.messages.limit}
                                </div>
                                {plugin.quotas.stale && (
                                  <div className="text-[10px]">(reset en attente)</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {plugin.quotas ? (
                              <span
                                className="text-xs text-muted-foreground"
                                title={`UTC ${plugin.quotas.resets_at}`}
                              >
                                {formatResetCountdown(plugin.quotas.resets_at)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`cursor-help ${heartbeatAgeColor(
                                    ageSeconds(plugin.last_seen_age_s, plugin.last_seen),
                                  )}`}
                                >
                                  {formatAge(ageSeconds(plugin.last_seen_age_s, plugin.last_seen))}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                Heartbeat (toutes les 15 à 60 s, TTL Redis 90 s).
                                Indique uniquement que le background service worker
                                du plugin est vivant — pas qu'il scrape. Voir la
                                colonne « Worker actif » pour l'activité réelle.
                                <div className="mt-1 font-mono text-[10px] opacity-80">
                                  UTC {plugin.last_seen}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const state = workerActivityState(plugin);
                              // Helper local : convertit un epoch ms (timestamp persiste
                              // cote plugin v1.4+) en delta "il y a Xs" lisible.
                              const fmtMs = (ms?: number | null) =>
                                !ms ? "—" : formatLastSeen(new Date(ms).toISOString());
                              return (
                                <div className="space-y-1">
                                  {plugin.linkedin_checkpoint?.active && (
                                    // Indicateur prioritaire : worker en pause sur une
                                    // « Vérification LinkedIn requise ». Placé en tête de
                                    // cellule car c'est le signal le plus actionnable.
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 text-xs font-semibold text-amber-700 cursor-help">
                                          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                                          Vérification LinkedIn requise
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-sm space-y-1">
                                        <div className="font-medium text-amber-800">
                                          Worker en pause — vérification LinkedIn
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">Type :</span>{" "}
                                          <code>{plugin.linkedin_checkpoint.type ?? "—"}</code>
                                        </div>
                                        {plugin.linkedin_checkpoint.detected_at && (
                                          <div>
                                            <span className="text-muted-foreground">En pause depuis :</span>{" "}
                                            {fmtMs(plugin.linkedin_checkpoint.detected_at)}
                                          </div>
                                        )}
                                        <div className="text-[10px] text-muted-foreground">
                                          LinkedIn exige une vérification de sécurité
                                          (checkpoint / challenge / authwall). Le plugin met le
                                          worker en pause jusqu'à résolution manuelle dans le
                                          navigateur ; le polling reprend automatiquement ensuite.
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={`cursor-help ${state.color}`}>
                                        {state.label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm space-y-1">
                                      <div>
                                        <span className="text-muted-foreground">Type :</span>{" "}
                                        <code>{plugin.last_job_type ?? "—"}</code>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Authentifié :</span>{" "}
                                        {nextJobAuthLabel(plugin)}
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Tier :</span>{" "}
                                        <code>{plugin.tier ?? "—"}</code>
                                      </div>
                                      {plugin.blocked_bucket && (
                                        <div>
                                          <span className="text-muted-foreground">Bucket bloqué :</span>{" "}
                                          <code>{plugin.blocked_bucket}</code>
                                        </div>
                                      )}
                                      <div className="border-t pt-1 space-y-0.5 text-[11px]">
                                        <div>
                                          <span className="text-muted-foreground">Alarme heartbeat :</span>{" "}
                                          {fmtMs(plugin.last_alarm_fired_at?.heartbeat)}
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">Alarme job :</span>{" "}
                                          {fmtMs(plugin.last_alarm_fired_at?.job_check)}
                                          {plugin.job_check_period_minutes != null && (
                                            <span className="text-muted-foreground">
                                              {" "}(période {plugin.job_check_period_minutes} min)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="border-t pt-1 text-[10px] text-muted-foreground">
                                        Reflète POST /api/worker/next-job. Polling content
                                        script LinkedIn = 5s. {">"} 30 s = tab LinkedIn fermée
                                        ou content script muet.
                                      </div>
                                      {plugin.last_next_job_call && (
                                        <div className="font-mono text-[10px] opacity-80">
                                          UTC {plugin.last_next_job_call}
                                        </div>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                  {plugin.last_worker_error && isWorkerErrorAlert(plugin) && (
                                    // Erreur plugin persistee dans chrome.storage.local,
                                    // remontee via heartbeat. Visible inline (pas dans le
                                    // tooltip) car c'est le signal le plus actionable pour
                                    // diagnostiquer un worker muet. Plan 5.1: only an
                                    // error younger than 30 min and not transient
                                    // (backoffice verdict); the others stay in the drawer.
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="text-[10px] text-red-600 truncate max-w-[180px] cursor-help">
                                          ⚠ {plugin.last_worker_error.source}/
                                          {plugin.last_worker_error.reason}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-sm space-y-1">
                                        <div className="font-medium text-red-700">
                                          Dernière erreur worker
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">Source :</span>{" "}
                                          <code>{plugin.last_worker_error.source}</code>
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">Reason :</span>{" "}
                                          <code>{plugin.last_worker_error.reason}</code>
                                        </div>
                                        {plugin.last_worker_error.message && (
                                          <div>
                                            <span className="text-muted-foreground">Msg :</span>{" "}
                                            <code className="break-all">
                                              {plugin.last_worker_error.message}
                                            </code>
                                          </div>
                                        )}
                                        <div className="text-[10px] text-muted-foreground">
                                          {fmtMs(plugin.last_worker_error.ts)}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          {/* Toggles interactifs : stopPropagation pour ne pas
                              ouvrir le drawer de détail en cliquant un switch. */}
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="w-12 cursor-help text-[10px] uppercase text-muted-foreground">
                                      Plugin
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    Coupe les jobs de scraping (enrichissements
                                    candidats/écoles/entreprises, recherches, offres
                                    d'emploi) pour CE plugin. Les invitations &
                                    messages de campagne continuent.
                                  </TooltipContent>
                                </Tooltip>
                                <Switch
                                  checked={!plugin.scraping_disabled}
                                  disabled={scrapingPending === `worker:${plugin.worker_id}`}
                                  onCheckedChange={(v) => handleToggleWorkerScraping(plugin, v)}
                                  aria-label="Scraping du plugin"
                                />
                              </div>
                              {!plugin.is_anonymous && plugin.user_id && (
                                <div className="flex items-center gap-1.5">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="w-12 cursor-help text-[10px] uppercase text-muted-foreground">
                                        Compte
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      Coupe le scraping pour TOUS les plugins
                                      authentifiés de ce compte (les anonymes ne sont
                                      pas concernés). Les invitations & messages de
                                      campagne continuent.
                                    </TooltipContent>
                                  </Tooltip>
                                  <Switch
                                    checked={!plugin.account_scraping_disabled}
                                    disabled={scrapingPending === `account:${plugin.user_id}`}
                                    onCheckedChange={(v) => handleToggleAccountScraping(plugin, v)}
                                    aria-label="Scraping du compte"
                                  />
                                </div>
                              )}
                              {plugin.account_scraping_disabled && (
                                <Badge variant="destructive" className="text-[10px]">
                                  Compte désactivé
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Total: {total} plugin(s) connecté(s)
                  {pageCount > 1 && (
                    <span className="ml-1">
                      · affichage {rangeStart}–{rangeEnd}
                    </span>
                  )}
                </div>
                {pageCount > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setPage(safePage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Précédent
                    </Button>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      Page {safePage} / {pageCount}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage >= pageCount}
                      onClick={() => setPage(safePage + 1)}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <PluginDetailSheet
          plugin={plugins.find((p) => p.worker_id === selectedId) ?? null}
          onClose={() => setSelectedId(null)}
          onResetDone={loadPlugins}
        />
      </SidebarInset>
    </TooltipProvider>
  );
}
